import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Sparkles, ShieldCheck, Network, RefreshCw } from 'lucide-react';
import { LANDING_IMAGES } from '../../lib/landingImages';

const POINTS = [
  { icon: Sparkles, key: 'v2_why_point1' },
  { icon: ShieldCheck, key: 'v2_why_point2' },
  { icon: Network, key: 'v2_why_point3' },
  { icon: RefreshCw, key: 'v2_why_point4' },
];

export const WhyAgrosV2 = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'tr';
  const darkImg = lang === 'en' ? LANDING_IMAGES.whyDarkEn : LANDING_IMAGES.whyDarkTr;
  const lightImg = lang === 'en' ? LANDING_IMAGES.whyLightEn : LANDING_IMAGES.whyLightTr;

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="v2-why">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-[140px] bg-indigo-700/15" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[120px] bg-fuchsia-700/12" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - visual (image in dark, neon placeholder in light) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative aspect-square w-full max-w-md mx-auto"
            data-testid="v2-why-visual"
          >
            {/* Dark mode: real branded image */}
            <div className="hidden dark:block absolute inset-0">
              <div className="absolute -inset-6 rounded-3xl bg-fuchsia-600/20 blur-3xl animate-neon-pulse pointer-events-none" />
              <div className="relative w-full h-full rounded-3xl overflow-hidden border border-fuchsia-500/30 shadow-[0_0_60px_rgba(176,38,255,0.35)]">
                <img
                  {...darkImg}
                  sizes="(min-width: 640px) 448px, 100vw"
                  alt="AgrosJet 3+ years of field experience"
                  className="w-full h-full object-cover"
                  loading="lazy" decoding="async"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-background/40 via-transparent to-transparent" />
              </div>
            </div>

            {/* Light mode: real branded daytime image */}
            <div className="dark:hidden absolute inset-0">
              <div className="absolute -inset-4 rounded-3xl bg-orange-400/15 blur-3xl pointer-events-none" />
              <div className="relative w-full h-full rounded-3xl overflow-hidden border border-orange-300/50 shadow-[0_8px_40px_rgba(255,106,0,0.18)]">
                <img
                  {...lightImg}
                  sizes="(min-width: 640px) 448px, 100vw"
                  alt="AgrosJet 3+ years of field experience"
                  className="w-full h-full object-cover"
                  loading="lazy" decoding="async"
                />
              </div>
            </div>
          </motion.div>

          {/* Right - content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-3 py-1 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-700 dark:text-orange-300 text-xs uppercase tracking-widest font-semibold mb-5">
              {t('v2_why_badge')}
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl text-foreground leading-[1.05] tracking-tight">
              {t('v2_why_title')}{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-fuchsia-500">
                {t('v2_why_title_accent')}
              </span>
            </h2>

            <div className="mt-7 space-y-4">
              <p className="text-foreground/75 text-base sm:text-lg leading-relaxed">
                {t('v2_why_desc1')}
              </p>
              <p className="text-foreground/65 text-base sm:text-lg leading-relaxed">
                {t('v2_why_desc2')}
              </p>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {POINTS.map(({ icon: Icon, key }, i) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  className="flex items-start gap-3 p-3.5 rounded-xl bg-foreground/[0.04] border border-border"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500/30 to-purple-700/20 border border-fuchsia-500/30 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-fuchsia-700 dark:text-fuchsia-300" strokeWidth={1.8} />
                  </div>
                  <span className="text-sm text-foreground/80 font-medium pt-1.5">{t(key)}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
